<template>
    <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="800"
        :fullscreen="isMobile" persistent @keydown.esc="chiudi">
        <v-card>
            <v-card-title class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-calendar-plus</v-icon>
                Nuova manutenzione
            </v-card-title>

            <v-card-text>
                <v-stepper v-model="step" flat>
                    <v-stepper-header>
                        <v-stepper-item title="Pianificazione" value="1" />
                        <v-divider />
                        <v-stepper-item title="Conferma" value="2" />
                    </v-stepper-header>

                    <div class="stepper-scroll-area">
                        <v-stepper-window>
                            <!-- Step 1: dati pianificazione -->
                            <v-stepper-window-item value="1">
                                <v-form ref="formRef">
                                    <v-select v-model="form.tipoManutenzioneId" :items="tipiManutenzione"
                                        item-title="nome" item-value="id" label="Tipo manutenzione"
                                        :rules="[v => !!v || 'Obbligatorio']" />
                                    <v-text-field v-model="form.dataPrevista" type="date" label="Data prevista"
                                        :rules="[v => !!v || 'Obbligatorio']" />
                                    <v-textarea v-model="form.descrizione" label="Descrizione" rows="2" />
                                    <v-checkbox v-model="form.ricorrente"
                                        label="Rendi ricorrente (pianifica subito le prossime occorrenze, a data fissa)"
                                        hint="Se non selezionato, alla chiusura verrà comunque generata una sola prossima occorrenza, calcolata dalla data di completamento effettiva."
                                        persistent-hint />
                                    <v-checkbox v-model="form.richiedeFermoMacchina" label="Richiede il fermo macchina"
                                        hint="Abilita il passaggio &quot;Inizia&quot; per tracciare il tempo di fermo effettivo"
                                        persistent-hint />
                                </v-form>

                                <v-divider class="my-2" />

                                <v-switch v-model="mostraMateriali" color="primary" density="compact" hide-details
                                    label="Materiali previsti per l'intervento" />

                                <div v-if="mostraMateriali" class="mt-2">
                                    <div v-for="(riga, idx) in materialiPrevisti" :key="idx"
                                        class="d-flex align-center mb-2">
                                        <v-select v-model="riga.ricambioId" :items="ricambiDisponibili"
                                            item-title="nome" item-value="id" label="Materiale" density="compact"
                                            hide-details class="mr-2" />
                                        <v-text-field v-model.number="riga.quantita" type="number" label="Qtà"
                                            density="compact" hide-details style="max-width: 100px" class="mr-2" />
                                        <v-btn icon variant="text" size="small" color="error"
                                            @click="materialiPrevisti.splice(idx, 1)">
                                            <v-icon>mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                    <v-btn variant="outlined" size="small" prepend-icon="mdi-plus"
                                        @click="materialiPrevisti.push({ ricambioId: null, quantita: 1 })">
                                        Aggiungi materiale
                                    </v-btn>
                                </div>
                            </v-stepper-window-item>

                            <!-- Step 2: conferma -->
                            <v-stepper-window-item value="2">
                                <v-card variant="outlined" class="rounded-lg pa-3 mb-3">
                                    <div class="text-caption text-medium-emphasis">Macchina</div>
                                    <div class="font-weight-medium mb-2">{{ macchinaNome }}</div>

                                    <div class="text-caption text-medium-emphasis">Tipo manutenzione</div>
                                    <div class="font-weight-medium mb-2">{{ tipoSelezionatoNome }}</div>

                                    <div class="text-caption text-medium-emphasis">Data prevista</div>
                                    <div class="font-weight-medium mb-2">{{ formattaData(form.dataPrevista) }}</div>

                                    <v-chip v-if="form.richiedeFermoMacchina" size="small" color="orange" variant="flat"
                                        class="mb-2">
                                        Richiede fermo macchina
                                    </v-chip>

                                    <template v-if="materialiValidi.length">
                                        <div class="text-caption text-medium-emphasis">Materiali previsti</div>
                                        <div v-for="riga in materialiValidi" :key="riga.ricambioId" class="text-body-2">
                                            {{ nomeRicambio(riga.ricambioId) }} — {{ riga.quantita }}
                                        </div>
                                    </template>
                                </v-card>

                                <v-alert v-if="form.ricorrente" type="info" variant="tonal" density="comfortable"
                                    class="rounded-lg">
                                    <div class="font-weight-medium">Pianificazione ricorrente</div>
                                    <div class="text-body-2">
                                        Verranno generate subito le prossime occorrenze a cadenza fissa, fino
                                        all'orizzonte di anni configurato per l'azienda (Mia Azienda).
                                    </div>
                                </v-alert>

                                <v-alert v-else-if="prossimaDataPreview" type="info" variant="tonal"
                                    density="comfortable" class="rounded-lg">
                                    <div class="font-weight-medium">Prossima manutenzione pianificata</div>
                                    <div class="text-body-2">
                                        Il tipo "{{ tipoSelezionatoNome }}" ha una frequenza configurata: alla chiusura
                                        verrà generata automaticamente una nuova manutenzione, con scadenza calcolata
                                        dalla data di completamento effettiva (in anteprima, se completata oggi:
                                        <strong>{{ formattaData(prossimaDataPreview) }}</strong>).
                                    </div>
                                </v-alert>
                            </v-stepper-window-item>
                        </v-stepper-window>
                    </div>
                </v-stepper>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn variant="text" @click="chiudi">Annulla</v-btn>
                <v-btn v-if="step === '2'" variant="text" @click="step = '1'">Indietro</v-btn>
                <v-btn v-if="step === '1'" color="primary" @click="vaiAConferma">Avanti</v-btn>
                <v-btn v-else color="primary" :loading="salvataggio" @click="salva">Conferma e pianifica</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: "DialogNuovaManutenzione",

    props: {
        modelValue: { type: Boolean, default: false },
        macchinaId: { type: [Number, String], required: true },
    },
    emits: ["update:modelValue", "salvato"],

    data() {
        return {
            step: "1",
            salvataggio: false,
            form: this.formVuoto(),
            mostraMateriali: false,
            materialiPrevisti: [],
        };
    },

    computed: {
        isMobile() {
            return this.$vuetify.display.smAndDown;
        },
        tipiManutenzione() {
            return this.$store.getters["tipiManutenzione/lista"];
        },
        ricambiDisponibili() {
            return this.$store.getters["ricambi/lista"];
        },
        macchinaNome() {
            const m = this.$store.getters["macchine/corrente"];
            return m && m.id === Number(this.macchinaId) ? m.nome : `Macchina #${this.macchinaId}`;
        },
        tipoSelezionato() {
            return this.tipiManutenzione.find(t => t.id === this.form.tipoManutenzioneId) || null;
        },
        tipoSelezionatoNome() {
            return this.tipoSelezionato ? this.tipoSelezionato.nome : "";
        },
        materialiValidi() {
            if (!this.mostraMateriali) return [];
            return this.materialiPrevisti.filter(r => r.ricambioId && r.quantita > 0);
        },
        prossimaDataPreview() {
            if (!this.tipoSelezionato || !this.form.dataPrevista) return null;
            return this.calcolaProssimaData(
                this.form.dataPrevista,
                this.tipoSelezionato.frequenzaValore,
                this.tipoSelezionato.frequenzaUnita
            );
        },
    },

    watch: {
        modelValue(val) {
            if (val) {
                this.step = "1";
                this.form = this.formVuoto();
                this.mostraMateriali = false;
                this.materialiPrevisti = [];
                this.$store.dispatch("tipiManutenzione/carica");
                this.$store.dispatch("ricambi/carica");
                this.$store.dispatch("macchine/caricaDettaglio", this.macchinaId);
            }
        },
    },

    methods: {
        formVuoto() {
            return {
                tipoManutenzioneId: null,
                dataPrevista: new Date().toISOString().substr(0, 10),
                descrizione: "",
                ricorrente: false,
                richiedeFermoMacchina: false,
            };
        },

        nomeRicambio(id) {
            const r = this.ricambiDisponibili.find(x => x.id === id);
            return r ? r.nome : "";
        },

        formattaData(iso) {
            if (!iso) return "";
            return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", {
                day: "numeric", month: "long", year: "numeric",
            });
        },

        // Anteprima client-side della prossima scadenza, calcolata con la stessa
        // logica di AutomazioneScadenze.CalcolaProssimaData lato backend. Il backend
        // serializza l'enum FrequenzaUnita come stringa (JsonStringEnumConverter),
        // quindi qui si confronta per nome ("Giorni"/"Settimane"/"Mesi"/"Anni"), non
        // per valore numerico. Solo a scopo informativo nello step di conferma.
        calcolaProssimaData(dataIso, frequenzaValore, frequenzaUnita) {
            if (!frequenzaValore || !frequenzaUnita) return null;
            const d = new Date(dataIso + "T00:00:00");
            switch (frequenzaUnita) {
                case "Giorni": d.setDate(d.getDate() + frequenzaValore); break;
                case "Settimane": d.setDate(d.getDate() + frequenzaValore * 7); break;
                case "Mesi": d.setMonth(d.getMonth() + frequenzaValore); break;
                case "Anni": d.setFullYear(d.getFullYear() + frequenzaValore); break;
                default: return null;
            }
            return this.toIsoLocale(d);
        },

        // Come toIsoLocale in Calendario.vue: evita di passare per toISOString(),
        // che convertendo in UTC sposta la data indietro di un giorno nei fusi
        // orari positivi (es. Italia, UTC+1/+2) quando l'ora locale è vicina alla
        // mezzanotte.
        toIsoLocale(d) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const g = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${g}`;
        },

        vaiAConferma() {
            if (!this.$refs.formRef.validate()) return;
            this.step = "2";
        },

        chiudi() {
            this.$emit("update:modelValue", false);
        },

        async salva() {
            this.salvataggio = true;
            try {
                await this.$store.dispatch("manutenzioni/pianifica", {
                    macchinaId: this.macchinaId,
                    tipoManutenzioneId: this.form.tipoManutenzioneId,
                    dataPrevista: this.form.dataPrevista,
                    descrizione: this.form.descrizione,
                    ricorrente: this.form.ricorrente,
                    richiedeFermoMacchina: this.form.richiedeFermoMacchina,
                    ricambiPrevisti: this.materialiValidi,
                });
                this.$emit("salvato");
                this.chiudi();
            } catch (error) {
                console.log(error);
            }
            this.salvataggio = false;
        },
    },
};
</script>

<style scoped>
.stepper-scroll-area {
    height: 460px;
    overflow-y: auto;
}
</style>
